import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const haversineM = (a, b) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

/**
 * Envoi adaptatif de la position livreur/chauffeur :
 * - en course : toutes les 5 s
 * - en ligne sans course : toutes les 30 s, ou dès 100 m parcourus
 * - batterie < 20 % (hors charge) et sans course : toutes les 60 s
 */
export function useAdaptiveTracking({ courierId, position, online, activeCourse }) {
  const last = useRef({ ts: 0, pos: null });
  const [lowBattery, setLowBattery] = useState(false);

  useEffect(() => {
    if (!navigator.getBattery) return;
    let battery;
    const update = () => setLowBattery(!battery.charging && battery.level < 0.2);
    navigator.getBattery().then((b) => {
      battery = b;
      update();
      b.addEventListener("levelchange", update);
      b.addEventListener("chargingchange", update);
    }).catch(() => {});
    return () => {
      if (!battery) return;
      battery.removeEventListener("levelchange", update);
      battery.removeEventListener("chargingchange", update);
    };
  }, []);

  const intervalMs = activeCourse ? 5000 : lowBattery ? 60000 : 30000;
  const latest = useRef(null);

  useEffect(() => {
    if (!online || !courierId) return;
    const send = (pos) => {
      last.current = { ts: Date.now(), pos };
      axios.post(`${API}/gps/update`, {
        courier_id: courierId,
        latitude: pos.lat,
        longitude: pos.lng,
        speed: pos.speed,
        heading: pos.heading,
        accuracy: pos.accuracy,
      }).catch(() => {});
    };
    latest.current = position ? { ...position, send } : latest.current;
    if (position) {
      const moved = last.current.pos ? haversineM(last.current.pos, position) : Infinity;
      const due = Date.now() - last.current.ts >= intervalMs || (!activeCourse && moved >= 100);
      if (due) send(position);
    }
  }, [position, online, courierId, activeCourse, intervalMs]);

  // Battement régulier : renvoie la dernière position même à l'arrêt (watchPosition ne se déclenche pas)
  useEffect(() => {
    if (!online || !courierId) return;
    const t = setInterval(() => {
      const p = latest.current;
      if (p && !document.hidden && Date.now() - last.current.ts >= intervalMs - 250) p.send(p);
    }, intervalMs);
    return () => clearInterval(t);
  }, [online, courierId, intervalMs]);

  return { intervalMs, lowBattery };
}
